/**
 * The Extraction pane's "Extracted details" rows, computed once and shared
 * with the full-screen card viewer (#327). Staff compare a member ID digit by
 * digit against the photo, so the viewer must show the SAME rows the pane
 * shows — a second derivation would drift, and a viewer that disagrees with
 * the pane behind it is worse than no viewer.
 *
 * Extracted verbatim from ExtractionPane (SmsThreadView.tsx); the #309 field
 * states and the #267 substitution gate keep their original semantics.
 */
import {
  PAYER_SUBSTITUTION_MIN_CONFIDENCE,
  resolvePayerDiffers,
  samePayerName,
} from '../../shared/payerMatch'
import { cardFieldRead, type CardFieldRead } from './cardReadState'
import { fmtDob } from './smsThread'
import { sameFieldValue, type CorrectableField } from './checkCorrection'
import type { RecoveryExtraction, StatedField } from '../services/recoveryCases'

export interface CardDetailRow {
  label: string
  state: CardFieldRead
  /**
   * The check-identity field(s) this row corrects (#349).
   *
   * Carried on the row rather than matched by label downstream: the editing
   * surfaces have to know WHICH field a typed value lands on, and deriving that
   * from the visible text would break the moment a label is reworded. Cardholder
   * is one row over two fields — the card prints one name line, and #309's read
   * states are defined over the pair.
   */
  fields: CorrectableField[]
  /** #267 — this row is part of the payer/member-ID pair the re-run
   *  substitutes; renders the "Suggested by VitalRev AI" marker. */
  ai?: boolean
  /**
   * The value shown is what the CHECK ASKED, not what the card read.
   *
   * Surfaces caption themselves from this: a panel of asked values under "what
   * the AI read from the photos" is simply a false statement, and it is the
   * common case on any pane that has ever been corrected.
   */
  fromAsked?: boolean
  /**
   * The value came from the patient answering in WRITING, not off the card.
   * The agent turn merges a texted answer into the same read as the OCR
   * (found + confidence 1), so without this the surfaces cannot tell the two
   * apart — and the viewer captions its panel as what was read from the photo.
   * A DOB the patient typed is a perfectly good answer; it just isn't evidence
   * the card prints one, which is a different question for the staff member
   * deciding whether to trust it.
   */
  stated?: boolean
}

export interface CardDetailRowsInput {
  extraction: RecoveryExtraction | null
  /** DOB we already hold from the record — a card that doesn't print one is
   *  then not a question (#309). */
  recordDob?: string | null
  /** The payer the failed check actually queried — the #267 badge input. */
  recordPayerName?: string | null
  /** The case's patient-texted answers. Key present ⇒ the value shown for that
   *  field is the patient's statement (a whole-set rebuild re-applies these
   *  over the photo read, so presence is enough — no value comparison). */
  statedFields?: Partial<Record<StatedField, string>> | null
  /**
   * What the check actually ASKED the payer — `requestSubscriber` with the set
   * identity as the legacy fallback, plus the resolved payer name.
   *
   * This is the row's value when it has one, and the OCR read is not (#349).
   * The pane is one surface now, so a row has to hold one value, and the
   * question staff are answering is "is this what we should be asking?" — which
   * the card read stops being an answer to the moment anyone corrects it. After
   * a correction the read still says what the photo said; only this moves.
   *
   * The card read is still the evidence, still on screen, and still what the
   * three unread states describe — a row falls back to them only where the
   * check itself holds nothing.
   */
  asked?: Partial<Record<CorrectableField, string>> | null
}

/**
 * Mirrors the backend's substitution gate through the SAME resolver
 * (`resolvePayerDiffers`): a decided verdict is trusted, an unconfirmed null
 * never shows the badge (the fold will re-ask), and only a pre-gate doc falls
 * back to the local string comparison. Either way the swap needs the member id
 * read too — the id travels WITH the payer, and the re-run never swaps without
 * it — so the badge never claims a substitution that won't happen.
 */
export function payerSwapForBadge(
  extraction: RecoveryExtraction | null,
  recordPayerName?: string | null,
): boolean {
  const d = extraction?.data
  const found = d?.found
  return resolvePayerDiffers(
    extraction?.payerDiffers,
    () => !!found?.payerName
      && (extraction?.confidence?.payerName ?? 0) >= PAYER_SUBSTITUTION_MIN_CONFIDENCE
      && !samePayerName(d?.payerName, recordPayerName),
  ) && !!found?.memberId
}

/**
 * What the card read actually achieved, for the actions a pane can honestly
 * offer (#349).
 *
 * Read off the ROWS rather than the extraction status so the footer and the
 * rows can never disagree: `heldOnRecord` turns an unread DOB into a non-issue,
 * and a status alone cannot see that.
 */
export interface CardReadSummary {
  /** At least one field came off the card. */
  anyRead: boolean
  /** A field was printed but illegible — the only state a clearer photo fixes. */
  anyUnreadable: boolean
  /** Nothing usable at all: no re-check to offer, whatever staff press. */
  nothingReadable: boolean
}

export function cardReadSummary(rows: CardDetailRow[]): CardReadSummary {
  const anyRead = rows.some((r) => r.state.kind === 'read')
  return {
    anyRead,
    anyUnreadable: rows.some((r) => r.state.kind === 'unreadable'),
    nothingReadable: rows.length > 0 && !anyRead,
  }
}

/**
 * The four rows, in the pane's order.
 *
 * A row shows what the check ASKED where it has a value, and falls back to the
 * card read otherwise (#349). #309's three unread states survive that fallback:
 * "not printed on this card" and "printed but illegible" are different answers
 * and still read differently.
 */
export function buildCardDetailRows(
  { extraction, recordDob, recordPayerName, statedFields, asked }: CardDetailRowsInput,
): CardDetailRow[] {
  const d = extraction?.data
  const found = d?.found
  const absent = d?.absent
  const payerSwapped = payerSwapForBadge(extraction, recordPayerName)
  const cardholder = [d?.firstName, d?.lastName].filter(Boolean).join(' ') || null
  const stated = statedFields ?? {}
  const A = asked ?? {}

  /**
   * The asked value wins; the card's own read is the fallback.
   *
   * A row is one value, and after a correction the two disagree on purpose —
   * the photo still says what it said. Showing the read there would report a
   * value nobody is asking the payer about any more.
   */
  const askedFirst = (
    fields: CorrectableField[], fromCard: CardFieldRead,
  ): CardFieldRead => {
    const value = fields.map((f) => (A[f] ?? '').trim()).filter(Boolean).join(' ')
    return value ? { kind: 'read', value } : fromCard
  }

  /**
   * Provenance belongs to the CARD read, so it may only be shown while the row
   * is still displaying that read. Once a correction replaces the value, "the
   * patient texted this" describes something no longer on screen — and so does
   * the #267 badge: "the re-check runs with the card's payer and member ID" is
   * a claim about the card's values, false on the on-file value it would
   * otherwise decorate. Both markers go through this gate.
   */
  const provenance = (fields: CorrectableField[], fromCard: CardFieldRead): boolean => {
    const shown = askedFirst(fields, fromCard)
    if (shown.kind !== 'read' || fromCard.kind !== 'read') return fromCard === shown
    return fields.some((f) => sameFieldValue(f, shown.value, fromCard.value))
  }

  return [
    (() => {
      const card = cardFieldRead({
        value: d?.memberId ?? null, found: found?.memberId, absent: absent?.memberId,
      })
      return {
        label: 'Member ID',
        fields: ['memberId'] as CorrectableField[],
        state: askedFirst(['memberId'], card),
        fromAsked: !!(A.memberId ?? '').trim(),
        ai: payerSwapped && provenance(['memberId'], card),
        stated: !!stated.memberId && provenance(['memberId'], card),
      }
    })(),
    (() => {
      const card = cardFieldRead({
        value: d?.payerName ?? null, found: found?.payerName, absent: absent?.payerName,
      })
      return {
        label: 'Payer',
        fields: ['payerName'] as CorrectableField[],
        state: askedFirst(['payerName'], card),
        fromAsked: !!(A.payerName ?? '').trim(),
        ai: payerSwapped && provenance(['payerName'], card),
        stated: !!stated.payerName && provenance(['payerName'], card),
      }
    })(),
    (() => {
      const card = cardFieldRead({
        value: d?.dateOfBirth ? fmtDob(d.dateOfBirth) : null,
        found: found?.dateOfBirth,
        absent: absent?.dateOfBirth,
        heldOnRecord: !!recordDob,
      })
      // The asked DOB is stored ISO; the row reads like the card's.
      const askedDob = (A.dateOfBirth ?? '').trim()
      const shown: CardFieldRead = askedDob
        ? { kind: 'read', value: fmtDob(askedDob) }
        : card
      return {
        label: 'Date of birth',
        fields: ['dateOfBirth'] as CorrectableField[],
        state: shown,
        fromAsked: !!askedDob,
        stated: !!stated.dateOfBirth
          && shown.kind === 'read' && card.kind === 'read' && shown.value === card.value,
      }
    })(),
    (() => {
      const card = cardFieldRead({
        value: cardholder,
        found: found?.firstName || found?.lastName,
        // Names are one row from two fields — call it not-printed only when the
        // card carries neither.
        absent: absent ? absent.firstName && absent.lastName : undefined,
      })
      const fields: CorrectableField[] = ['firstName', 'lastName']
      return {
        label: 'Cardholder',
        fields,
        state: askedFirst(fields, card),
        fromAsked: fields.some((f) => !!(A[f] ?? '').trim()),
        // One row, two fields: the patient stating either name makes the value
        // shown here partly their words, so it must not read as pure card text.
        stated: !!(stated.firstName || stated.lastName) && provenance(fields, card),
      }
    })(),
  ]
}
